/**
 * Recipient signing page: collect missing required fields shown in the signature section.
 */

export function getRecipientSignerIndex(totalSignatures) {
  if (totalSignatures === 2) return 1
  return 0
}

function layoutLabel(item, labelOverrides, t) {
  if (item.id === 'terms') return labelOverrides?.terms ?? t('app.termsAgree')
  if (item.id === 'fullName') return labelOverrides?.fullName ?? t('app.fullName')
  if (item.id === 'phone') return labelOverrides?.phone ?? t('app.phone')
  if (item.id === 'signature') return labelOverrides?.signature ?? t('app.signature')
  if (item.id === 'date') return labelOverrides?.date ?? t('app.date')
  return item.label || item.id
}

/** Fields the recipient must fill in (read-only / decorative types are excluded). */
export function isRecipientFillableFieldType(fieldType) {
  return fieldType !== 'textDisplay' && fieldType !== 'spacer'
}

function isCustomFieldValueMissing(cf, val) {
  if (cf.type === 'textDisplay') return false
  if (cf.type === 'checkbox') return val !== true
  if (cf.type === 'signature') return !val
  if (cf.type === 'text') return !String(val || '').trim()
  if (cf.type === 'date') return !val
  if (cf.type === 'number') return val === undefined || val === null || val === ''
  if (cf.type === 'picklist') return !val
  if (cf.type === 'time') return !val
  return !String(val ?? '').trim()
}

/**
 * @returns {Array<{ fieldKey: string, fieldId: string, label: string }>}
 */
export function collectSignerMissingFieldItems({
  signer,
  signerIdx,
  activeLayoutItems,
  customFields,
  labelOverrides,
  t,
  includeDate,
  includeTerms,
  includeFullName,
  includePhone,
  includeSignature,
}) {
  if (!signer) return []
  const items = []
  const activeById = new Map(
    activeLayoutItems.filter((f) => !f.isSpacer).map((f) => [f.id, f]),
  )

  const maybeAdd = (fieldId, label) => {
    if (!activeById.has(fieldId)) return
    items.push({ fieldKey: `${signerIdx}:${fieldId}`, fieldId, label })
  }

  if (includeDate && activeById.has('date') && !signer.date) {
    maybeAdd('date', layoutLabel(activeById.get('date'), labelOverrides, t))
  }
  if (includeTerms && activeById.has('terms') && !signer.agreed) {
    maybeAdd('terms', layoutLabel(activeById.get('terms'), labelOverrides, t))
  }
  if (includeFullName && activeById.has('fullName') && !signer.fullName?.trim()) {
    maybeAdd('fullName', layoutLabel(activeById.get('fullName'), labelOverrides, t))
  }
  if (includePhone && activeById.has('phone') && !signer.phone?.trim()) {
    maybeAdd('phone', layoutLabel(activeById.get('phone'), labelOverrides, t))
  }
  if (includeSignature && activeById.has('signature') && !signer.signatureDataUrl) {
    maybeAdd('signature', layoutLabel(activeById.get('signature'), labelOverrides, t))
  }

  for (const cf of customFields) {
    if (!activeById.has(cf.id)) continue
    if (!isRecipientFillableFieldType(cf.type)) continue
    const val = (signer.customFields || {})[cf.id]
    if (!isCustomFieldValueMissing(cf, val)) continue
    const layoutItem = activeById.get(cf.id)
    const label = layoutItem?.label || cf.label || 'Field'
    maybeAdd(cf.id, label)
  }

  return items
}

export function getBracketSlotLabels(parts, emptySlotIndexes) {
  const labelByIndex = new Map()
  for (const p of parts) {
    if (p.type === 'slot') labelByIndex.set(p.index, p.label || 'Field')
  }
  return emptySlotIndexes.map((idx) => labelByIndex.get(idx) || `Field ${idx + 1}`)
}
